/* =========================
   PEGUU — CART ORDER SUMMARY
========================= */

(() => {
  "use strict";

  const QR_PRODUCT_CODE = "qr_sticker";
  const SAMPLE_PACK_PRODUCT_CODE = "sample_pack";
  const SAMPLE_PACK_API_URL =
    window.PeguuApi.endpoint("samplePack");
  const DESIGN_FILE_DB = "peguu-design-files";
  const DESIGN_FILE_STORE = "files";
  const PENDING_MATERIAL_STORAGE_KEY = "peguu:selected-material";
  const PENDING_ORDER_STORAGE_KEY = "peguu:sticker-order";
  const PENDING_DESIGN_STORAGE_KEY = "peguu:sticker-design";
  const CART_EDIT_STORAGE_KEY = "peguu:cart-edit";
  const QR_CONFIG_STORAGE_KEY = "peguu:qr-config";
  const activeLocalPreviewUrls = new Set();
  const UPLOADED_DESIGN_PLACEHOLDER =
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="172" height="172" viewBox="0 0 172 172">
        <rect width="172" height="172" rx="24" fill="#f1ecfa"/>
        <path d="M55 31h42l25 25v73c0 7-6 12-13 12H55c-7 0-13-5-13-12V43c0-7 6-12 13-12Z" fill="#fff" stroke="#835ec7" stroke-width="6"/>
        <path d="M97 31v25h25" fill="none" stroke="#835ec7" stroke-width="6" stroke-linejoin="round"/>
        <path d="m61 92 14 14 31-34" fill="none" stroke="#6ac14c" stroke-width="9" stroke-linecap="round" stroke-linejoin="round"/>
        <text x="86" y="157" text-anchor="middle" font-family="Arial,sans-serif" font-size="15" font-weight="700" fill="#574676">Archivo cargado</text>
      </svg>
    `)}`;

  const isSamplePackMode = () =>
    new URLSearchParams(
      window.location.search
    ).get("mode") === "sample-pack";

  let samplePackRequest = null;

  const getSamplePack = async () => {
    if (samplePackRequest) {
      return samplePackRequest;
    }

    samplePackRequest = (async () => {
      const response = await fetch(SAMPLE_PACK_API_URL, {
        headers: { Accept: "application/json" },
      });
      let payload = null;

      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }

      const material = payload?.data;

      if (!response.ok || payload?.success !== true) {
        throw new Error(
          payload?.message ||
          "No fue posible consultar el paquete de muestras."
        );
      }

      if (
        !material?.id ||
        material?.active === false ||
        material?.productCode !== SAMPLE_PACK_PRODUCT_CODE
      ) {
        throw new Error(
          "El paquete de muestras no está disponible."
        );
      }

      return material;
    })().catch((error) => {
      samplePackRequest = null;
      throw error;
    });

    return samplePackRequest;
  };

  const buildSamplePackSummary = (material) => {
    const purchase = material?.purchase || {};
    const unitPrice = Number(purchase.unitPrice);
    const lineTotal = Number(purchase.lineTotal);
    const resolvedUnitPrice = Number.isFinite(unitPrice)
      ? unitPrice
      : null;
    const resolvedLineTotal = Number.isFinite(lineTotal)
      ? lineTotal
      : resolvedUnitPrice;

    return {
      source: "sample-pack",
      items: [{
        materialId: material.id,
        materialName: material.name,
        materialImageUrl: material.imageUrl,
        productCode: material.productCode,
        quantity: 1,
        includedQuantity: Number.isFinite(Number(purchase.quantity))
          ? Number(purchase.quantity)
          : null,
        unitPrice: resolvedUnitPrice,
        lineTotal: resolvedLineTotal,
        currency: purchase.currency || "MXN",
      }],
      pricing: {
        subtotal: resolvedLineTotal,
        discountAmount: 0,
        shippingCost: null,
        total: resolvedLineTotal,
      },
    };
  };

  const waitForPeguuCart = (
    timeoutMs = 5000
  ) => {
    if (
      typeof window.PeguuCart
        ?.getCurrentCart ===
        "function"
    ) {
      return Promise.resolve(
        window.PeguuCart
      );
    }

    return new Promise((resolve, reject) => {
      let timeoutId = null;

      const cleanup = () => {
        document.removeEventListener(
          "peguu:cart:ready",
          handleReady
        );

        if (timeoutId) {
          window.clearTimeout(
            timeoutId
          );
        }
      };

      const handleReady = () => {
        if (
          typeof window.PeguuCart
            ?.getCurrentCart !==
            "function"
        ) {
          return;
        }

        cleanup();
        resolve(window.PeguuCart);
      };

      document.addEventListener(
        "peguu:cart:ready",
        handleReady
      );

      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(
          new Error(
            "El carrito no está disponible en este momento."
          )
        );
      }, timeoutMs);

      handleReady();
    });
  };

  const normalizeText = (value) =>
    String(value ?? "").trim();

  const parseSessionJson = (key) => {
    const storedValue = window.sessionStorage.getItem(key);

    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue);
    } catch (error) {
      console.warn(`Peguu Cart Summary: no se pudo leer ${key}.`, error);
      return null;
    }
  };

  const firstDefined = (...values) => values.find((value) => (
    value !== undefined && value !== null && value !== ""
  ));

  const normalizeNumber = (value) => {
    if (value === undefined || value === null || value === "") {
      return null;
    }

    const number = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(number) ? number : null;
  };

  const getPendingOrder = () => {
    const material = parseSessionJson(PENDING_MATERIAL_STORAGE_KEY);
    const order = parseSessionJson(PENDING_ORDER_STORAGE_KEY);
    const design = parseSessionJson(PENDING_DESIGN_STORAGE_KEY);
    const qrConfig = parseSessionJson(QR_CONFIG_STORAGE_KEY);

    if (!material || !order) {
      return null;
    }

    const quantity = normalizeNumber(firstDefined(
      order?.quote?.quantity,
      order?.quantity,
      order?.pieces,
      order?.pieceQuantity,
      order?.selectedQuantity,
      order?.cantidad,
      order?.cantidadPiezas
    ));
    const unitPrice = normalizeNumber(firstDefined(
      order?.quote?.unitPrice,
      order?.unitPrice,
      order?.pricePerUnit
    ));
    const subtotal = normalizeNumber(firstDefined(
      order?.quote?.subtotal,
      order?.subtotal,
      order?.total,
      unitPrice !== null && quantity !== null ? unitPrice * quantity : null
    ));
    const productCode = normalizeText(firstDefined(
      material?.productCode,
      material?.product_code,
      order?.material?.productCode,
      order?.productCode
    ));
    const pendingDesign = design?.primaryFile
      ? {
          ...design.primaryFile,
          uploadLater:
            design.uploadLater === true,
        }
      : design || order?.design || null;

    return {
      persistedCartItemId:
        order?.cartAddition?.added === true
          ? normalizeText(
              order?.cartAddition?.itemId
            )
          : "",
      materialId: firstDefined(material?.id, material?.materialId, order?.materialId),
      materialName: normalizeText(firstDefined(
        material?.name,
        material?.materialName,
        material?.title,
        order?.material?.name,
        order?.materialName,
        order?.productName,
        "Stickers personalizados"
      )),
      materialImageUrl: normalizeText(firstDefined(
        material?.imageUrl,
        material?.image,
        material?.thumbnailUrl,
        order?.material?.imageUrl,
        order?.materialImageUrl
      )),
      productCode,
      widthCm: normalizeNumber(firstDefined(
        order?.measurements?.widthCm,
        order?.widthCm
      )),
      heightCm: normalizeNumber(firstDefined(
        order?.measurements?.heightCm,
        order?.heightCm
      )),
      quantity,
      unitPrice,
      lineTotal: subtotal,
      subtotal,
      currency: normalizeText(firstDefined(order?.quote?.currency, order?.currency)) || "MXN",
      design: pendingDesign,
      qrConfig: productCode === QR_PRODUCT_CODE
        ? qrConfig || order?.qrConfig || order?.qr || null
        : null,
    };
  };

  const includePendingOrder = (
    root,
    cartItems = []
  ) => {
    if (root.dataset.cartOrderSummaryIncludePending !== "true") {
      return null;
    }

    const pendingOrder =
      getPendingOrder();

    if (!pendingOrder) {
      return null;
    }

    const persistedCartItemId =
      normalizeText(
        pendingOrder.persistedCartItemId
      );

    const alreadyInCart =
      Boolean(persistedCartItemId) &&
      cartItems.some((item) =>
        normalizeText(
          firstDefined(
            item?.id,
            item?._id,
            item?.itemId,
            item?.cartItemId,
            item?.localItemId
          )
        ) === persistedCartItemId
      );

    return alreadyInCart
      ? null
      : pendingOrder;
  };

  const getCartItemId = (item) =>
    normalizeText(
      firstDefined(
        item?.id,
        item?._id,
        item?.itemId,
        item?.cartItemId,
        item?.localItemId
      )
    );

  const getCartCouponCodes = (cart) =>
    (Array.isArray(cart?.coupons)
      ? cart.coupons
      : cart?.coupon
        ? [cart.coupon]
        : [])
      .map((coupon) =>
        normalizeText(coupon?.code)
      )
      .filter(Boolean);

  const quoteCartWithPendingOrder = async (
    root,
    cart
  ) => {
    const cartItems =
      Array.isArray(cart?.items)
        ? cart.items
        : [];
    const pendingItem =
      includePendingOrder(
        root,
        cartItems
      );

    if (
      !pendingItem ||
      typeof window.PeguuCart
        ?.quoteItems !== "function"
    ) {
      return cart;
    }

    const editState =
      parseSessionJson(
        CART_EDIT_STORAGE_KEY
      );
    const editingItemId =
      editState?.mode === "edit"
        ? normalizeText(
            editState.itemId
          )
        : "";
    const editingItemIndex =
      editingItemId
        ? cartItems.findIndex(
            (item) =>
              getCartItemId(item) ===
              editingItemId
          )
        : -1;

    if (
      editingItemId &&
      editingItemIndex < 0
    ) {
      return cart;
    }

    const previewItems =
      editingItemIndex >= 0
        ? cartItems.map(
            (item, index) =>
              index === editingItemIndex
                ? {
                    ...item,
                    ...pendingItem,
                  }
                : item
          )
        : [
            ...cartItems,
            pendingItem,
          ];
    const quoteItems =
      previewItems.map(
        (item, index) => ({
          ...item,
          localItemId:
            getCartItemId(item) ||
            `pending-preview-${index}`,
        })
      );

    try {
      const quoted =
        await window.PeguuCart
          .quoteItems(
            quoteItems,
            getCartCouponCodes(cart)
          );

      if (!quoted?.pricing) {
        return cart;
      }

      if (!getPendingOrder()) {
        return window.PeguuCart
          .getCurrentCart();
      }

      const cartCouponCodes =
        getCartCouponCodes(cart);
      const quotedCouponCodes =
        getCartCouponCodes(quoted);
      const keepsCartCoupon =
        cartCouponCodes.some(
          (code) =>
            !quotedCouponCodes
              .includes(code)
        );
      const retainedDiscount =
        keepsCartCoupon
          ? normalizeNumber(
              cart?.pricing
                ?.discountAmount
            ) || 0
          : 0;
      const quotedDiscount =
        normalizeNumber(
          quoted.pricing
            .discountAmount
        ) || 0;
      const quotedTotal =
        normalizeNumber(
          quoted.pricing.total
        );
      const pricing =
        retainedDiscount > 0
          ? {
              ...quoted.pricing,
              discountAmount:
                quotedDiscount +
                retainedDiscount,
              total:
                quotedTotal === null
                  ? quotedTotal
                  : Math.max(
                      0,
                      quotedTotal -
                        retainedDiscount
                    ),
            }
          : quoted.pricing;

      return {
        ...cart,
        coupon:
          keepsCartCoupon
            ? cart?.coupon
            : quoted.coupon ??
              cart?.coupon,
        coupons:
          keepsCartCoupon
            ? cart?.coupons
            : Array.isArray(quoted.coupons)
            ? quoted.coupons
            : cart?.coupons,
        couponError:
          quoted.couponError ??
          null,
        pricing:
          pricing,
        pricingIncludesPendingOrder:
          true,
      };
    } catch (error) {
      console.warn(
        "Peguu Cart Summary: no fue posible cotizar el carrito con el pedido pendiente.",
        error
      );

      return cart;
    }
  };

  const normalizeComparableText = (value) =>
    normalizeText(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const escapeHtml = (value) =>
    normalizeText(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const hasNumericValue = (value) =>
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value));

  const formatCurrency = (value, currency = "MXN") => {
    if (!hasNumericValue(value)) {
      return "—";
    }

    try {
      return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: normalizeText(currency) || "MXN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value));
    } catch (error) {
      return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value));
    }
  };

  const formatQuantity = (value) => {
    if (!hasNumericValue(value)) {
      return "—";
    }

    return `${new Intl.NumberFormat("es-MX", {
      maximumFractionDigits: 0,
    }).format(Number(value))} pzas`;
  };

  const formatCentimeters = (value) =>
    new Intl.NumberFormat("es-MX", {
      maximumFractionDigits: 2,
    }).format(Number(value));

  const formatSize = (item) => {
    if (item?.productCode === SAMPLE_PACK_PRODUCT_CODE) {
      return "No aplica";
    }

    const widthCm = normalizeNumber(firstDefined(
      item?.widthCm,
      item?.measurements?.widthCm,
      item?.quote?.widthCm
    ));
    const heightCm = normalizeNumber(firstDefined(
      item?.heightCm,
      item?.measurements?.heightCm,
      item?.quote?.heightCm
    ));

    if (
      !hasNumericValue(widthCm) ||
      !hasNumericValue(heightCm) ||
      widthCm <= 0 ||
      heightCm <= 0
    ) {
      return "";
    }

    return `${formatCentimeters(widthCm)} × ${formatCentimeters(heightCm)} cm`;
  };

  const resolveMaterialTotal = (item) => {
    return normalizeNumber(
      firstDefined(
        item?.lineTotal,
        item?.subtotal
      )
    );
  };

  const resolveQrConfig = (item) =>
    item?.qrConfig || item?.qr || item?.qrConfiguration || null;

  const resolveQrPosition = (item) => {
    const config = resolveQrConfig(item);
    return config?.position || config?.posicion || "";
  };

  const resolveDestinationUrl = (item) => {
    const config = resolveQrConfig(item);
    return normalizeText(
      config?.destinationUrl ||
      config?.link ||
      config?.url
    );
  };

  const isQrItem = (item) => {
    if (
      normalizeComparableText(item?.productCode) ===
      QR_PRODUCT_CODE
    ) {
      return true;
    }

    const config = resolveQrConfig(item);
    const flowType = normalizeComparableText(
      config?.flowType || config?.flow_type || config?.type
    );

    return (
      flowType === "qr" ||
      Boolean(resolveQrPosition(item) && resolveDestinationUrl(item))
    );
  };

  const formatQrPosition = (value) => {
    const normalizedValue = normalizeComparableText(value);
    const labels = {
      arriba: "Arriba",
      top: "Arriba",
      abajo: "Abajo",
      bottom: "Abajo",
      izquierda: "Izquierda",
      left: "Izquierda",
      derecha: "Derecha",
      right: "Derecha",
    };

    if (labels[normalizedValue]) {
      return labels[normalizedValue];
    }

    if (!normalizedValue) {
      return "—";
    }

    return `${normalizedValue.charAt(0).toUpperCase()}${normalizedValue.slice(1)}`;
  };

  const resolveDesignName = (item) => {
    const design = item?.design;

    if (!design) {
      return "—";
    }

    if (design.uploadLater === true) {
      return "Lo subiré más tarde";
    }

    return normalizeText(
      design.fileName || design.filename || design.name
    ) || "—";
  };

  const resolveDesignImage = (item) => {
    const design = item?.design;

    if (!design || design.uploadLater === true) {
      return "";
    }

    const previewUrl = normalizeText(
      design.thumbnailUrl ||
      design.previewUrl ||
      design.thumbnail_url ||
      design.preview_url ||
      design.thumbnail?.url ||
      design.preview?.url
    );

    if (previewUrl) {
      return previewUrl;
    }

    const visualUrl = normalizeText(
      design.fileUrl ||
      design.signedUrl ||
      design.downloadUrl ||
      design.publicUrl ||
      design.storageUrl ||
      design.imageUrl ||
      design.assetUrl ||
      design.secureUrl ||
      design.src ||
      design.file_url ||
      design.signed_url ||
      design.download_url ||
      design.public_url ||
      design.storage_url ||
      design.image_url ||
      design.asset_url ||
      design.secure_url ||
      design.url ||
      design.file?.url
    );

    if (!visualUrl) {
      return normalizeText(
        design.fileId || design.file_id
      )
        ? UPLOADED_DESIGN_PLACEHOLDER
        : "";
    }

    const fileName = normalizeText(
      design.fileName || design.filename || design.name
    );
    const extension = normalizeComparableText(
      design.extension || fileName.split(".").pop()
    );
    const fileType = normalizeComparableText(
      design.fileType ||
      design.mimeType ||
      design.contentType
    );

    if (
      extension &&
      !["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(extension) &&
      !fileType.startsWith("image/")
    ) {
      return "";
    }

    return visualUrl;
  };

  const resolveLocalPreviewKey = (item) => {
    const design = item?.design;
    const fileName = normalizeText(
      design?.fileName || design?.filename || design?.name
    );
    const extension = normalizeComparableText(
      design?.extension || fileName.split(".").pop()
    );

    if (
      !design ||
      design.uploadLater === true ||
      !["jpg", "jpeg", "png", "webp"].includes(extension)
    ) {
      return "";
    }

    return normalizeText(design.localPreviewKey);
  };

  const getLocalDesignFile = (key) => {
    if (!key || !window.indexedDB) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const request = indexedDB.open(DESIGN_FILE_DB, 1);

      request.onerror = () => resolve(null);
      request.onsuccess = () => {
        const database = request.result;

        if (!database.objectStoreNames.contains(DESIGN_FILE_STORE)) {
          database.close();
          resolve(null);
          return;
        }

        const getRequest = database
          .transaction(DESIGN_FILE_STORE, "readonly")
          .objectStore(DESIGN_FILE_STORE)
          .get(key);

        getRequest.onsuccess = () => {
          database.close();
          resolve(getRequest.result || null);
        };
        getRequest.onerror = () => {
          database.close();
          resolve(null);
        };
      };
    });
  };

  const resolveImage = (item) =>
    resolveDesignImage(item) || normalizeText(item?.materialImageUrl);

  const renderRow = (label, value, modifier = "") => `
    <div class="peguu-order-summary__row ${modifier}">
      <dt>${escapeHtml(label)}:</dt>
      <dd>${escapeHtml(value || "—")}</dd>
    </div>
  `;

  const renderEditAction = (item) => {
    const itemId =
      normalizeText(item?.id);

    if (
      !itemId ||
      item?.isEditingPreview === true ||
      item?.productCode ===
        SAMPLE_PACK_PRODUCT_CODE ||
      !["customer", "guest"]
        .includes(item?.source)
    ) {
      return "";
    }

    return `
      <button
        type="button"
        class="peguu-cart-order-summary__edit"
        data-cart-order-summary-edit
        data-item-id="${escapeHtml(itemId)}"
        data-source="${escapeHtml(item.source)}"
      >
        Editar pedido
      </button>
    `;
  };

  const renderItem = (item, index) => {
    const isSamplePack =
      item?.productCode ===
        SAMPLE_PACK_PRODUCT_CODE;
    const materialName = normalizeText(item?.materialName) || "—";
    const imageUrl = resolveImage(item);
    const materialImageUrl = normalizeText(item?.materialImageUrl);
    const localPreviewKey = resolveLocalPreviewKey(item);
    const destinationUrl = resolveDestinationUrl(item);
    const qrRows = isQrItem(item)
      ? [
          renderRow("Posición del QR", formatQrPosition(resolveQrPosition(item))),
          renderRow("URL destino", destinationUrl || "—", "peguu-order-summary__row--destination-url"),
        ].join("")
      : "";
    const includedQuantity = Number(
      item?.includedQuantity
    );
    const quantityLabel =
      isSamplePack &&
      Number.isFinite(includedQuantity) &&
      includedQuantity > 0
        ? `${formatQuantity(includedQuantity)} piezas`
        : formatQuantity(item?.quantity);
    const detailRows = isSamplePack
      ? [
          renderRow("Nombre", materialName),
          renderRow("Cantidad", quantityLabel),
          renderRow("Precio unitario", formatCurrency(item?.unitPrice, item?.currency), "peguu-order-summary__row--unit-price"),
        ].join("")
      : [
          renderRow("Material", materialName),
          renderRow("Cantidad", quantityLabel),
          qrRows,
          renderRow("Precio unitario", formatCurrency(item?.unitPrice, item?.currency), "peguu-order-summary__row--unit-price"),
          renderRow("Diseño", resolveDesignName(item), "peguu-order-summary__row--design"),
        ].join("");
    const imageMarkup = imageUrl
      ? `<img
          class="peguu-order-summary__image"
          src="${escapeHtml(imageUrl)}"
          alt="${escapeHtml(`Pedido en ${materialName}`)}"
          ${localPreviewKey
            ? `data-local-design-preview="${escapeHtml(localPreviewKey)}"`
            : ""}
          ${materialImageUrl && imageUrl !== materialImageUrl
            ? `data-material-image-fallback="${escapeHtml(materialImageUrl)}"`
            : ""}
          width="88"
          height="112"
        >`
      : `<span class="peguu-cart-order-summary__image-placeholder" aria-hidden="true">P</span>`;

    return `
      <article class="peguu-cart-order-summary__item">
        <span class="peguu-cart-order-summary__item-number">Producto ${index + 1}</span>
        <aside class="peguu-order-summary">
          <div class="peguu-order-summary__content">
            <div class="peguu-order-summary__top">
              <h2 class="peguu-order-summary__title">Resumen de<br>tu pedido</h2>
              <div class="peguu-order-summary__image-wrap">${imageMarkup}</div>
            </div>
            <dl class="peguu-order-summary__list">
              ${detailRows}
            </dl>
          </div>
          <div class="peguu-order-summary__total">
            <span>Precio total</span>
            <strong>${escapeHtml(formatCurrency(item?.lineTotal ?? item?.subtotal, item?.currency))}</strong>
          </div>
        </aside>
      </article>
    `;
  };

  const renderCompactItem = (item, index) => {
    const isSamplePack = item?.productCode === SAMPLE_PACK_PRODUCT_CODE;
    const materialName = normalizeText(item?.materialName) || "—";
    const imageUrl = resolveImage(item);
    const materialImageUrl = normalizeText(item?.materialImageUrl);
    const localPreviewKey = resolveLocalPreviewKey(item);
    const includedQuantity = Number(item?.includedQuantity);
    const quantityLabel = isSamplePack && Number.isFinite(includedQuantity) && includedQuantity > 0
      ? formatQuantity(includedQuantity)
      : formatQuantity(item?.quantity);
    const imageMarkup = imageUrl
      ? `<img class="peguu-order-summary__image" src="${escapeHtml(imageUrl)}"
          alt="${escapeHtml(`Pedido en ${materialName}`)}"
          ${localPreviewKey ? `data-local-design-preview="${escapeHtml(localPreviewKey)}"` : ""}
          ${materialImageUrl && imageUrl !== materialImageUrl ? `data-material-image-fallback="${escapeHtml(materialImageUrl)}"` : ""}
          width="88" height="112">`
      : `<span class="peguu-cart-order-summary__image-placeholder" aria-hidden="true">P</span>`;

    return `
      <article class="peguu-cart-order-summary__item" aria-label="Producto ${index + 1}">
        <div class="peguu-order-summary__image-wrap">${imageMarkup}</div>
        <dl class="peguu-cart-order-summary__item-details">
          ${renderRow("Material", materialName, "peguu-order-summary__row--material")}
          ${renderRow("Medida", formatSize(item), "peguu-order-summary__row--measurement")}
          ${renderRow("Cantidad", quantityLabel, "peguu-order-summary__row--quantity")}
          ${renderRow("Precio unit.", formatCurrency(item?.unitPrice, item?.currency), "peguu-order-summary__row--unit-price")}
          ${renderRow("Total de material", formatCurrency(resolveMaterialTotal(item), item?.currency), "peguu-order-summary__row--material-total")}
        </dl>
      </article>`;
  };

  const renderSamplePackItem = (item) => {
    const name = normalizeText(item?.materialName) || "—";
    const imageUrl = normalizeText(item?.materialImageUrl);
    const includedQuantity = Number(item?.includedQuantity);
    const quantityLabel = Number.isFinite(includedQuantity) && includedQuantity > 0
      ? `1 paquete (incluye ${new Intl.NumberFormat("es-MX").format(includedQuantity)} muestras)`
      : "1 paquete";
    const imageMarkup = imageUrl
      ? `<img
          class="peguu-order-summary__image"
          src="${escapeHtml(imageUrl)}"
          alt="${escapeHtml(name)}"
          width="88"
          height="112"
        >`
      : `<span class="peguu-cart-order-summary__image-placeholder" aria-hidden="true">P</span>`;

    return `
      <article class="peguu-cart-order-summary__item peguu-cart-order-summary__item--sample-pack">
        <aside class="peguu-order-summary">
          <div class="peguu-order-summary__content">
            <div class="peguu-order-summary__top">
              <h2 class="peguu-order-summary__title">Resumen de<br>tu pedido</h2>
              <div class="peguu-order-summary__image-wrap">${imageMarkup}</div>
            </div>
            <dl class="peguu-order-summary__list">
              ${renderRow("Nombre", name)}
              ${renderRow("Cantidad", quantityLabel)}
              ${renderRow("Precio unitario", formatCurrency(item?.unitPrice, item?.currency), "peguu-order-summary__row--unit-price")}
            </dl>
          </div>
          <div class="peguu-order-summary__total">
            <span>Precio total</span>
            <strong>${escapeHtml(formatCurrency(item?.lineTotal, item?.currency))}</strong>
          </div>
        </aside>
      </article>
    `;
  };

  class PeguuCartOrderSummary {
    constructor(root) {
      this.root = root;
      this.loading = root.querySelector("[data-cart-order-summary-loading]");
      this.error = root.querySelector("[data-cart-order-summary-error]");
      this.empty = root.querySelector("[data-cart-order-summary-empty]");
      this.filled = root.querySelector("[data-cart-order-summary-filled]");
      this.items = root.querySelector("[data-cart-order-summary-items]");
      this.carousel = root.querySelector("[data-cart-order-summary-carousel]");
      this.carouselPrevious = root.querySelector("[data-cart-order-summary-previous]");
      this.carouselNext = root.querySelector("[data-cart-order-summary-next]");
      this.carouselStatus = root.querySelector("[data-cart-order-summary-carousel-status]");
      this.subtotal = root.querySelector("[data-cart-order-summary-subtotal]");
      this.discountRow = root.querySelector("[data-cart-order-summary-discount-row]");
      this.discountLabel = root.querySelector("[data-cart-order-summary-discount-label]");
      this.discount = root.querySelector("[data-cart-order-summary-discount]");
      this.shipping = root.querySelector("[data-cart-order-summary-shipping]");
      this.total = root.querySelector("[data-cart-order-summary-total]");
      this.grandTotal = root.querySelector("[data-cart-order-summary-grand-total]");
      this.compactSubtotal = null;
      this.compactDiscountRow = null;
      this.compactDiscountLabel = null;
      this.compactDiscount = null;
      this.compactShipping = null;
      this.isCompactLayout =
        root.dataset.cartOrderSummaryCompact === "true";
      this.showCompactDescription =
        root.dataset.cartOrderSummaryShowDescription !== "false";
      this.showCompactHeader =
        root.dataset.cartOrderSummaryShowHeader !== "false";
      this.currentCart = null;
      this.refreshPromise = null;
      this.carouselIndex = 0;

      this.carouselPrevious?.addEventListener("click", () => {
        this.goToCarouselItem(this.carouselIndex - 1);
      });
      this.carouselNext?.addEventListener("click", () => {
        this.goToCarouselItem(this.carouselIndex + 1);
      });
      this.items?.addEventListener("scroll", () => {
        window.requestAnimationFrame(() => this.syncCarouselPosition());
      }, { passive: true });
      this.items?.addEventListener(
        "click",
        async (event) => {
          const button =
            event.target.closest(
              "[data-cart-order-summary-edit]"
            );

          if (!button) {
            return;
          }

          event.preventDefault();
          button.disabled = true;
          this.showError("");

          try {
            if (
              typeof window.PeguuCart
                ?.startEdit !==
              "function"
            ) {
              throw new Error(
                "No fue posible abrir el configurador."
              );
            }

            await window.PeguuCart.startEdit(
              button.dataset.itemId,
              button.dataset.source,
              `${window.location.pathname}${window.location.search}`
            );
          } catch (error) {
            button.disabled = false;
            this.showError(
              error?.message ||
              "No fue posible editar el producto."
            );
          }
        }
      );
    }

    getCarouselItems() {
      return Array.from(
        this.items?.querySelectorAll(".peguu-cart-order-summary__item") || []
      );
    }

    goToCarouselItem(index, behavior = "smooth") {
      const items = this.getCarouselItems();
      if (!items.length) return;

      const nextIndex = Math.max(0, Math.min(index, items.length - 1));
      this.carouselIndex = nextIndex;
      this.items?.scrollTo({
        left: items[nextIndex].offsetLeft - items[0].offsetLeft,
        behavior,
      });
      this.updateCarouselControls(items.length);
    }

    syncCarouselPosition() {
      const items = this.getCarouselItems();
      if (!items.length || !this.items) return;

      const currentLeft = this.items.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;

      items.forEach((item, index) => {
        const itemLeft = item.offsetLeft - items[0].offsetLeft;
        const distance = Math.abs(itemLeft - currentLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.carouselIndex = closestIndex;
      this.updateCarouselControls(items.length);
    }

    updateCarouselControls(itemCount = this.getCarouselItems().length) {
      if (this.carousel) {
        this.carousel.hidden = itemCount <= 1;
      }
      if (this.carouselPrevious) {
        this.carouselPrevious.disabled = this.carouselIndex <= 0;
      }
      if (this.carouselNext) {
        this.carouselNext.disabled = this.carouselIndex >= itemCount - 1;
      }
      if (this.carouselStatus) {
        this.carouselStatus.textContent = itemCount
          ? `${this.carouselIndex + 1} de ${itemCount}`
          : "";
      }
    }

    setLoading(
      isLoading,
      { replaceContent = false } = {}
    ) {
      if (this.loading) {
        this.loading.hidden = !isLoading;
      }

      if (
        isLoading &&
        replaceContent
      ) {
        this.filled.hidden = true;
        this.empty.hidden = true;
      }
    }

    showError(message) {
      if (!this.error) {
        return;
      }

      this.error.textContent = message;
      this.error.hidden = !message;
    }

    hydrateImageFallbacks() {
      this.items?.querySelectorAll("[data-material-image-fallback]").forEach((image) => {
        image.addEventListener("error", () => {
          const fallback = image.dataset.materialImageFallback;
          image.removeAttribute("data-material-image-fallback");
          if (fallback) {
            image.src = fallback;
          }
        }, { once: true });
      });
    }

    async hydrateLocalDesignPreviews() {
      activeLocalPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
      activeLocalPreviewUrls.clear();

      const images = this.items?.querySelectorAll(
        "[data-local-design-preview]"
      ) || [];

      await Promise.all(Array.from(images).map(async (image) => {
        const file = await getLocalDesignFile(
          image.dataset.localDesignPreview
        );

        if (!file || !String(file.type || "").startsWith("image/")) {
          return;
        }

        const previewUrl = URL.createObjectURL(file);
        activeLocalPreviewUrls.add(previewUrl);
        image.src = previewUrl;
      }));
    }

    render(cart, { preserveItems = false } = {}) {
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];
      const pendingItem = includePendingOrder(
        this.root,
        cartItems
      );
      const editState =
        parseSessionJson(
          CART_EDIT_STORAGE_KEY
        );
      const editingItemId =
        editState?.mode === "edit"
          ? normalizeText(
              editState.itemId
            )
          : "";
      const editingItemIndex =
        editingItemId
          ? cartItems.findIndex(
              (item) =>
                normalizeText(
                  firstDefined(
                    item?.id,
                    item?._id,
                    item?.itemId,
                    item?.cartItemId,
                    item?.localItemId
                  )
                ) ===
                editingItemId
            )
          : -1;
      const originalEditingItem =
        editingItemIndex >= 0
          ? cartItems[editingItemIndex]
          : null;
      const editingPreviewItem =
        pendingItem &&
        originalEditingItem
          ? {
              ...originalEditingItem,
              ...pendingItem,
              id:
                originalEditingItem.id,
              source:
                originalEditingItem.source,
              fileId:
                pendingItem.design?.fileId ||
                originalEditingItem.fileId ||
                null,
              fileId2:
                originalEditingItem.fileId2 ||
                null,
              isEditingPreview: true,
            }
          : null;
      const items = editingPreviewItem
        ? cartItems.map(
            (item, index) =>
              index === editingItemIndex
                ? editingPreviewItem
                : item
          )
        : pendingItem && !editingItemId
          ? [...cartItems, pendingItem]
          : cartItems;
      const cartPricing = cart?.pricing || {};
      const pendingTotal = normalizeNumber(pendingItem?.lineTotal) || 0;
      const originalEditingTotal =
        normalizeNumber(
          originalEditingItem?.lineTotal ??
          originalEditingItem?.subtotal
        ) || 0;
      const pendingDifference =
        editingPreviewItem
          ? pendingTotal -
            originalEditingTotal
          : pendingTotal;
      const pricing = cart?.pricingIncludesPendingOrder === true
        ? cartPricing
        : pendingItem &&
        (editingPreviewItem || !editingItemId)
        ? {
            ...cartPricing,
            subtotal: (normalizeNumber(cartPricing.subtotal) || 0) + pendingDifference,
            total: (normalizeNumber(cartPricing.total) || 0) + pendingDifference,
          }
        : cartPricing;
      const currency = normalizeText(items[0]?.currency) || "MXN";
      const hasItems = items.length > 0;

      this.currentCart = cart;
      this.empty.hidden = hasItems;
      this.filled.hidden = !hasItems;

      if (this.items && !preserveItems) {
        if (this.isCompactLayout) {
          const itemMarkup = items.map(renderCompactItem).join("");
          const headerMarkup = this.showCompactHeader
            ? `<h2 class="peguu-cart-order-summary__panel-title">Resumen de tu pedido</h2>`
            : "";
          const descriptionMarkup = this.showCompactDescription
            ? '<p class="peguu-cart-order-summary__panel-description">Revisa todos los productos de tu pedido antes de continuar.</p>'
            : "";
          this.items.innerHTML = `
            <div class="peguu-cart-order-summary__panel">
              ${headerMarkup}
              ${descriptionMarkup}
              <div class="peguu-cart-order-summary__product-list">${itemMarkup}</div>
            </div>
            <div class="peguu-cart-order-summary__pricing">
              <dl class="peguu-cart-order-summary__breakdown">
                <div class="peguu-cart-order-summary__breakdown-row">
                  <dt>Subtotal</dt>
                  <dd data-cart-order-summary-compact-subtotal>—</dd>
                </div>
                <div class="peguu-cart-order-summary__breakdown-row peguu-cart-order-summary__breakdown-row--discount" data-cart-order-summary-compact-discount-row hidden>
                  <dt data-cart-order-summary-compact-discount-label>Descuento</dt>
                  <dd data-cart-order-summary-compact-discount>—</dd>
                </div>
                <div class="peguu-cart-order-summary__breakdown-row">
                  <dt>Envío</dt>
                  <dd data-cart-order-summary-compact-shipping>Por calcular</dd>
                </div>
              </dl>
              <div class="peguu-cart-order-summary__grand-total">
                <span>Precio total</span>
                <strong data-cart-order-summary-grand-total>—</strong>
              </div>
            </div>`;
          this.grandTotal = this.items.querySelector("[data-cart-order-summary-grand-total]");
          this.compactSubtotal = this.items.querySelector("[data-cart-order-summary-compact-subtotal]");
          this.compactDiscountRow = this.items.querySelector("[data-cart-order-summary-compact-discount-row]");
          this.compactDiscountLabel = this.items.querySelector("[data-cart-order-summary-compact-discount-label]");
          this.compactDiscount = this.items.querySelector("[data-cart-order-summary-compact-discount]");
          this.compactShipping = this.items.querySelector("[data-cart-order-summary-compact-shipping]");
        } else {
          this.items.innerHTML = cart?.source === "sample-pack"
            ? items.map(renderSamplePackItem).join("")
            : items.map(renderItem).join("");
        }
        this.carouselIndex = 0;
        this.items.scrollLeft = 0;
        this.updateCarouselControls(items.length);
        this.hydrateImageFallbacks();
        void this.hydrateLocalDesignPreviews();
      }

      if (this.subtotal) {
        this.subtotal.textContent = formatCurrency(pricing.subtotal, currency);
      }

      if (this.compactSubtotal) {
        this.compactSubtotal.textContent = formatCurrency(pricing.subtotal, currency);
      }

      const discountAmount = Number(pricing.discountAmount);
      const hasDiscount =
        Number.isFinite(discountAmount) && discountAmount > 0;

      const subtotal =
        normalizeNumber(pricing.subtotal);

      const shippingCost =
        normalizeNumber(pricing.shippingCost) ?? 0;
      const apiTotal =
        normalizeNumber(pricing.total);
      const productsTotal =
        apiTotal !== null
          ? apiTotal
          : subtotal === null
            ? null
            : Math.max(
                0,
                subtotal -
                  (hasDiscount
                    ? discountAmount
                    : 0) +
                  shippingCost
              );

      if (this.discountRow) {
        this.discountRow.hidden = !hasDiscount;
      }

      if (this.compactDiscountRow) {
        this.compactDiscountRow.hidden = !hasDiscount;
      }

      if (this.discountLabel) {
        const couponCodes = (Array.isArray(cart?.coupons)
          ? cart.coupons
          : cart?.coupon
            ? [cart.coupon]
            : [])
          .map((coupon) => normalizeText(coupon?.code))
          .filter(Boolean);
        this.discountLabel.textContent = couponCodes.length
          ? `Descuento (${couponCodes.join(", ")})`
          : "Descuento";
      }

      if (this.compactDiscountLabel) {
        const couponCodes = (Array.isArray(cart?.coupons)
          ? cart.coupons
          : cart?.coupon
            ? [cart.coupon]
            : [])
          .map((coupon) => normalizeText(coupon?.code))
          .filter(Boolean);
        this.compactDiscountLabel.textContent = couponCodes.length
          ? `Descuento (${couponCodes.join(", ")})`
          : "Descuento";
      }

      if (this.discount) {
        this.discount.textContent = hasDiscount
          ? `−${formatCurrency(discountAmount, currency)}`
          : formatCurrency(0, currency);
      }

      if (this.compactDiscount) {
        this.compactDiscount.textContent = hasDiscount
          ? `−${formatCurrency(discountAmount, currency)}`
          : formatCurrency(0, currency);
      }

      if (this.shipping) {
        const freeShipping =
          pricing.freeShipping === true;

        this.shipping.textContent = freeShipping
          ? "Gratis"
          : "Con costo";
        this.shipping.classList.toggle(
          "is-free",
          freeShipping
        );

        if (this.compactShipping) {
          this.compactShipping.textContent = freeShipping
            ? "Gratis"
            : "Con costo";
          this.compactShipping.classList.toggle(
            "is-free",
            freeShipping
          );
        }
      }

      if (this.total) {
        this.total.textContent = formatCurrency(productsTotal, currency);
      }

      if (this.grandTotal) {
        this.grandTotal.textContent = formatCurrency(productsTotal, currency);
      }

      this.root.dispatchEvent(new CustomEvent("peguu:cart-summary:updated", {
        bubbles: true,
        detail: { cart },
      }));
    }

    async refresh(options = {}) {
      if (this.refreshPromise) {
        return this.refreshPromise;
      }

      this.refreshPromise = (async () => {
        this.setLoading(
          true,
          {
            replaceContent:
              options.replaceContent ===
              true
          }
        );
        this.showError("");

        try {
          const currentCart = isSamplePackMode()
            ? buildSamplePackSummary(
                await getSamplePack()
              )
            : options.cart ||
              await (
                await waitForPeguuCart()
              ).getCurrentCart();
          const cart = isSamplePackMode()
            ? currentCart
            : await quoteCartWithPendingOrder(
                this.root,
                currentCart
              );
          this.render(
            cart,
            {
              preserveItems:
                options.preserveItems ===
                true,
            }
          );
          return cart;
        } catch (error) {
          console.error("Peguu Cart Summary: no fue posible consultar el carrito.", error);
          this.showError(
            error?.message || "No pudimos actualizar tu carrito. Inténtalo nuevamente."
          );
          throw error;
        } finally {
          this.setLoading(false);
          this.refreshPromise = null;
        }
      })();

      return this.refreshPromise;
    }
  }

  const instances = new Set();
  let isAwaitingAuthenticationMerge =
    false;

  const initialize = (scope = document) => {
    const roots = scope.matches?.("[data-peguu-cart-order-summary]")
      ? [scope]
      : Array.from(scope.querySelectorAll("[data-peguu-cart-order-summary]"));

    roots.forEach((root) => {
      if (root.dataset.peguuInitialized === "true") {
        return;
      }

      root.dataset.peguuInitialized = "true";
      const instance = new PeguuCartOrderSummary(root);
      root.peguuCartOrderSummary = instance;
      instances.add(instance);
      instance.refresh().catch(() => {});
    });
  };

  const refreshAll = (event) => {
    const action = normalizeText(event?.detail?.action);
    const cart = event?.detail?.cart;
    const eventType =
      normalizeText(event?.type);
    const isAuthenticationUpdate =
      eventType ===
        "peguu:customer-authenticated";
    const isCartMerge =
      eventType ===
        "peguu:cart:merged";
    const isCartMergeFailure =
      eventType ===
        "peguu:cart:merge-failed";
    const isCouponUpdate =
      Boolean(cart) &&
      (action === "coupon" || action === "coupon-remove");
    const hasGuestItems =
      Boolean(
        window.PeguuCart
          ?.getGuestCart?.()
          ?.length
      );

    if (
      isAuthenticationUpdate &&
      hasGuestItems
    ) {
      isAwaitingAuthenticationMerge =
        true;
    }

    if (
      isCartMerge ||
      isCartMergeFailure
    ) {
      isAwaitingAuthenticationMerge =
        false;
    }

    const waitsForGuestCartMerge =
      isAuthenticationUpdate &&
      isAwaitingAuthenticationMerge;

    const positionSummaryAtTop = (
      instance,
      behavior = "auto"
    ) => {
      const summary =
        instance.root.closest(
          "[data-peguu-sticker-summary]"
        );

      if (
        !summary ||
        summary.classList.contains(
          "is-payment-expanded"
        )
      ) {
        return;
      }

      window.scrollTo({
        top: 0,
        left: 0,
        behavior
      });
    };

    instances.forEach((instance) => {
      if (instance.root.isConnected) {
        if (
          isAuthenticationUpdate
        ) {
          instance.setLoading(
            true,
            {
              replaceContent: true
            }
          );
          positionSummaryAtTop(
            instance,
            "smooth"
          );

          if (
            waitsForGuestCartMerge
          ) {
            return;
          }

          instance.refresh({
            replaceContent: true
          }).then(() => {
            positionSummaryAtTop(
              instance
            );
          }).catch(() => {});
        } else if (
          isCartMerge &&
          cart
        ) {
          instance.render(cart);
          instance.setLoading(false);

          window.requestAnimationFrame(
            () => {
              positionSummaryAtTop(
                instance
              );
            }
          );
        } else if (
          isCartMergeFailure
        ) {
          instance.refresh({
            replaceContent: true
          }).then(() => {
            positionSummaryAtTop(
              instance
            );
          }).catch(() => {});
        } else if (isCouponUpdate) {
          instance.refresh({
            cart,
            preserveItems: true,
          }).catch(() => {});
        } else {
          instance.refresh().catch(() => {});
        }
      } else {
        instances.delete(instance);
      }
    });
  };

  window.PeguuCartOrderSummary = Object.freeze({
    initialize,
    refreshAll,
  });

  window.PeguuSamplePackCheckout = Object.freeze({
    getMaterial: getSamplePack,
  });

  document.addEventListener("peguu:cart:updated", refreshAll);
  document.addEventListener("peguu:cart:refresh", refreshAll);
  document.addEventListener("peguu:cart:merged", refreshAll);
  document.addEventListener("peguu:cart:merge-failed", refreshAll);
  document.addEventListener("peguu:cart:ready", refreshAll);
  window.addEventListener("peguu:customer-authenticated", refreshAll);
  window.addEventListener("peguu:customer-logged-out", refreshAll);
  window.addEventListener("storage", (event) => {
    if (
      [
        "peguu:guest-cart",
        "peguu:customer-auth",
        "peguu:customer-cart-metadata",
      ].includes(event.key)
    ) {
      refreshAll();
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
